import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Save, MessageCircle, Info, ArrowLeftRight } from "lucide-react";

// Cada template: rótulo curto (lista), QUANDO é disparado (explicação),
// e as variáveis que fazem sentido ali (nem toda variável serve pra todo
// template - mostrar só as relevantes evita confusão e tentativa e erro).
const META = {
  billing_before_5: {
    label: "5 dias antes do vencimento",
    when: "Enviada automaticamente pelo agendador diário, 5 dias antes do vencimento, para boletos ainda em aberto.",
    vars: ["primeiro_nome", "nome", "valor", "vencimento", "pix_copia_cola", "login"],
  },
  billing_on_due: {
    label: "No dia do vencimento",
    when: "Enviada automaticamente no próprio dia do vencimento do boleto.",
    vars: ["primeiro_nome", "nome", "valor", "vencimento", "pix_copia_cola", "login"],
  },
  billing_after_5: {
    label: "5 dias após o vencimento",
    when: "Enviada automaticamente quando o boleto completa 5 dias de atraso.",
    vars: ["primeiro_nome", "nome", "valor", "vencimento", "pix_copia_cola", "login"],
  },
  billing_after_10: {
    label: "10 dias após o vencimento",
    when: "Enviada automaticamente quando o boleto completa 10 dias de atraso.",
    vars: ["primeiro_nome", "nome", "valor", "vencimento", "pix_copia_cola", "login"],
  },
  billing_after_15: {
    label: "15 dias após o vencimento",
    when: "Enviada automaticamente quando o boleto completa 15 dias de atraso.",
    vars: ["primeiro_nome", "nome", "valor", "vencimento", "pix_copia_cola", "login"],
  },
  billing_after_30: {
    label: "30 dias após o vencimento",
    when: "Enviada automaticamente quando o boleto completa 30 dias de atraso.",
    vars: ["primeiro_nome", "nome", "valor", "vencimento", "pix_copia_cola", "login"],
  },
  billing_after_45: {
    label: "45 dias após o vencimento",
    when: "Enviada automaticamente quando o boleto completa 45 dias de atraso.",
    vars: ["primeiro_nome", "nome", "valor", "vencimento", "pix_copia_cola", "login"],
  },
  birthday: {
    label: "Aniversário",
    when: "Enviada automaticamente no dia do aniversário do cliente — não fala de cobrança, é só uma mensagem de carinho.",
    vars: ["primeiro_nome", "nome"],
  },
  welcome_menu: {
    label: "Menu inicial (chatbot)",
    when: "Primeira mensagem que o cliente recebe ao iniciar uma conversa, antes de escolher qualquer opção. As opções numeradas em si são editadas na tela \"Menu do Bot\".",
    vars: ["primeiro_nome", "nome"],
  },
  boleto_delivery: {
    label: "Entrega do boleto — em dia",
    when: "Usada quando o cliente pede a 2ª via (painel, comando FATURA, chatbot ou cobrança em lote) e o boleto AINDA NÃO venceu.",
    vars: ["primeiro_nome", "nome", "valor", "vencimento", "login"],
    pairWith: "boleto_delivery_atrasado",
    pairNote: "Boleto vencido usa o outro template (\"Entrega do boleto — vencido\").",
  },
  boleto_delivery_atrasado: {
    label: "Entrega do boleto — vencido",
    when: "Usada na MESMA situação da anterior, mas quando o boleto JÁ está vencido — por isso tem variáveis extras pra mencionar o atraso.",
    vars: ["primeiro_nome", "nome", "valor", "valor_original", "vencimento", "dias_atraso", "multa_juros", "login"],
    pairWith: "boleto_delivery",
    pairNote: "Boleto em dia usa o outro template (\"Entrega do boleto — em dia\").",
  },
  menu_unknown_reply: {
    label: "Resposta para opção inválida",
    when: "Enviada quando o cliente responde algo que não é nenhuma das opções numeradas do menu.",
    vars: ["primeiro_nome"],
  },
  client_blocked: {
    label: "Acesso bloqueado",
    when: "Enviada automaticamente quando o mk-auth BLOQUEIA o acesso do cliente (monitorado a cada 5 min, dentro da janela segura de envio). Vai junto com o PIX, o QR Code e o PDF da fatura em atraso.",
    vars: ["primeiro_nome", "nome", "valor", "valor_original", "vencimento", "dias_atraso", "multa_juros", "qtd_faturas", "total_aberto", "login"],
  },
  human_chat_closed: {
    label: "Atendimento finalizado",
    when: "Enviada ao cliente quando um atendente clica em \"Encerrar agora\" na tela de Atendimentos, avisando que a conversa com o humano foi finalizada.",
    vars: ["primeiro_nome"],
  },
};

const CATEGORIES = [
  {
    title: "Cobrança automática",
    description: "Enviadas sozinhas pelo agendador diário, de acordo com quantos dias faltam ou já passaram do vencimento. Desativar uma delas só pula esse estágio específico — as outras continuam normais.",
    keys: ["billing_before_5", "billing_on_due", "billing_after_5", "billing_after_10", "billing_after_15", "billing_after_30", "billing_after_45"],
  },
  {
    title: "Aniversário",
    description: "Enviada automaticamente no dia do aniversário — não depende de boleto nenhum.",
    keys: ["birthday"],
  },
  {
    title: "Chatbot — entrega de boleto",
    description: "Enviadas quando alguém pede a 2ª via de um jeito manual (fora dos lembretes agendados acima): painel, comando FATURA, chatbot ou cobrança em lote.",
    keys: ["boleto_delivery", "boleto_delivery_atrasado"],
  },
  {
    title: "Chatbot — menu",
    description: "Mensagens do fluxo de atendimento automático, sem falar de valores ou vencimento.",
    keys: ["welcome_menu", "menu_unknown_reply"],
  },
  {
    title: "Bloqueio de acesso",
    description: "Mensagem enviada ao cliente logo depois que o mk-auth bloqueia o acesso dele por falta de pagamento. Pode ser desligada em Configurações.",
    keys: ["client_blocked"],
  },
  {
    title: "Atendimento humano",
    description: "Mensagem enviada ao cliente quando o atendente encerra a conversa pausada pela tela de Atendimentos.",
    keys: ["human_chat_closed"],
  },
];

const VAR_LABELS = {
  primeiro_nome: "primeiro nome do cliente",
  nome: "nome completo",
  valor: "valor a pagar",
  valor_original: "valor original (sem multa/juros)",
  vencimento: "data de vencimento",
  titulo: "número do boleto",
  linha_digitavel: "linha digitável",
  pix_copia_cola: "código PIX copia-e-cola",
  url_boleto: "link do boleto",
  login: "login do cliente no mk-auth",
  dias_atraso: "quantos dias de atraso",
  qtd_faturas: "quantas faturas em aberto",
  total_aberto: "soma de todas as faturas em aberto",
  multa_juros: "valor da multa + juros",
};

const PREVIEW_VALUES = {
  primeiro_nome: "João",
  nome: "João Silva",
  valor: "129,90",
  valor_original: "120,00",
  vencimento: "05/03/2026",
  titulo: "12345",
  linha_digitavel: "34191.79001 01043.510047 91020.150008 1 12345678901234",
  pix_copia_cola: "00020126...5204000053039865802BR...6304ABCD",
  url_boleto: "https://boleto/exemplo",
  login: "joaosilva",
  dias_atraso: "7",
  multa_juros: "9,90",
};

function renderPreview(body) {
  let text = body || "…";
  for (const [key, value] of Object.entries(PREVIEW_VALUES)) {
    text = text.replaceAll(`{${key}}`, value);
  }
  return text;
}

export default function Templates() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/templates");
      const ordered = Object.keys(META)
        .map((k) => data.find((t) => t.key === k) || { key: k, body: "", enabled: true });
      setItems(ordered);
      setActive((prev) => (prev ? ordered.find((i) => i.key === prev.key) || ordered[0] : ordered[0]));
    } catch (ex) {
      toast.error(formatApiError(ex));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const insertVar = (v) => {
    if (!active) return;
    setActive((a) => ({ ...a, body: (a.body || "") + `{${v}}` }));
  };

  const save = async () => {
    if (!active) return;
    setSaving(true);
    try {
      await api.put(`/templates/${active.key}`, active);
      toast.success("Template salvo");
      load();
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setSaving(false);
    }
  };

  const meta = active ? META[active.key] : null;
  const isScheduled = active ? active.key.startsWith("billing_") || active.key === "birthday" : false;

  return (
    <div className="space-y-8">
      <header className="border-b border-slate-800 pb-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-400">Configuração</div>
        <h1 className="font-outfit text-4xl font-bold tracking-tight text-white mt-1">Templates</h1>
        <p className="text-sm text-slate-500 mt-2">
          Personalize o texto de cada mensagem automática. Clique num item na lista à esquerda pra ver quando ele é
          usado e quais variáveis fazem sentido nele.
        </p>
      </header>

      <section className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <Card className="bg-slate-900/50 border-slate-800 p-3 lg:col-span-1 space-y-4">
          {CATEGORIES.map((cat) => (
            <div key={cat.title}>
              <div className="px-2 py-1">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{cat.title}</div>
                <div className="text-[11px] text-slate-600 leading-snug mt-0.5">{cat.description}</div>
              </div>
              <div className="space-y-1 mt-1">
                {cat.keys.map((key) => {
                  const it = items.find((i) => i.key === key);
                  if (!it) return null;
                  return (
                    <button
                      key={key}
                      data-testid={`tpl-item-${key}`}
                      onClick={() => setActive(it)}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors duration-200 ${
                        active?.key === key
                          ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30"
                          : "text-slate-400 hover:text-white hover:bg-slate-800/70 border border-transparent"
                      }`}
                    >
                      <div className="flex items-center gap-1.5">
                        <span>{META[key].label}</span>
                        {!it.enabled && (
                          <span className="text-[9px] uppercase tracking-wide text-slate-600 border border-slate-700 rounded px-1">
                            inativo
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </Card>

        {active && meta && (
          <Card className="bg-slate-900/50 border-slate-800 p-6 lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Editando</div>
                <div className="font-outfit text-lg text-white font-semibold">{meta.label}</div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-xs text-slate-500">Ativo</span>
                <Switch
                  data-testid="tpl-enabled-switch"
                  checked={active.enabled}
                  onCheckedChange={(v) => setActive((a) => ({ ...a, enabled: v }))}
                />
              </div>
            </div>

            <div className="flex items-start gap-2 text-xs text-slate-400 bg-slate-950/50 border border-slate-800 rounded-md p-3">
              <Info className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
              <div className="space-y-1.5">
                <p>{meta.when}</p>
                <p className="text-slate-500">
                  {isScheduled
                    ? "Se desativar, esse aviso específico deixa de ser enviado — os outros estágios de cobrança continuam normais."
                    : "Se desativar, o sistema volta a usar o texto padrão de fábrica em vez do seu texto."}
                </p>
                {meta.pairNote && (
                  <p className="flex items-center gap-1.5 text-amber-300/80">
                    <ArrowLeftRight className="w-3 h-3 shrink-0" /> {meta.pairNote}
                  </p>
                )}
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-1.5">
                Variáveis disponíveis aqui
              </div>
              <div className="flex flex-wrap gap-2">
                {meta.vars.map((v) => (
                  <button
                    key={v}
                    data-testid={`tpl-var-${v}`}
                    onClick={() => insertVar(v)}
                    title={VAR_LABELS[v]}
                    className="px-2 py-1 rounded-md text-xs font-mono-jb border border-slate-700 text-emerald-300 hover:bg-emerald-500/10 transition-colors duration-200"
                  >
                    {`{${v}}`}
                  </button>
                ))}
              </div>
            </div>

            <Textarea
              data-testid="tpl-body-textarea"
              value={active.body || ""}
              onChange={(e) => setActive((a) => ({ ...a, body: e.target.value }))}
              rows={12}
              className="bg-slate-950 border-slate-800 text-white font-mono-jb text-sm focus-visible:ring-emerald-500/40 focus-visible:border-emerald-500"
            />
            <Button
              data-testid="tpl-save-btn"
              onClick={save}
              disabled={saving}
              className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
            >
              <Save className="w-4 h-4 mr-2" /> Salvar template
            </Button>
          </Card>
        )}

        {active && (
          <Card className="bg-slate-900/50 border-slate-800 p-6 lg:col-span-1">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Preview WhatsApp</div>
            <div className="mt-3 rounded-2xl border border-slate-800 bg-slate-950 p-3 min-h-[300px]">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center">
                  <MessageCircle className="w-4 h-4 text-emerald-950" />
                </div>
                <div className="text-xs text-slate-400">Quitaí • agora</div>
              </div>
              <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-slate-100 text-sm p-3 whitespace-pre-wrap break-words leading-relaxed max-w-full overflow-hidden">
                {renderPreview(active.body)}
              </div>
            </div>
          </Card>
        )}
      </section>
    </div>
  );
}
